import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { RiskBadge } from "../components/RiskMeter";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, PlayCircle, MapPin, Storefront, TrendUp, CheckCircle, Buildings, Users, Lightning, ChartBar } from "@phosphor-icons/react";

const WHY_ICONS = [Lightning, ChartBar, Users, TrendUp, MapPin];
const WHY_DEFAULT = ["Strong Unit Economics", "Proven Business Model", "Experienced Leadership", "High Growth Industry", "Pan-India Expansion"];

export default function BrandDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [brand, setBrand] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.get(`/brands/${slug}`).then(({ data }) => setBrand(data)).catch(() => setNotFound(true)); }, [slug]);

  if (notFound) return <div className="px-6 py-32 text-center text-ebg-muted">Brand not found. <Link to="/explore" className="text-gold">Back to explore</Link></div>;
  if (!brand) return <div className="px-6 py-32 text-center text-ebg-muted font-data">Loading…</div>;

  const goInvest = () => {
    if (!user) return navigate("/login");
    if (user.kyc_status !== "verified") return navigate(`/kyc?next=/invest/${brand.slug}`);
    return navigate(`/invest/${brand.slug}`);
  };
  const why = brand.why_invest?.length ? brand.why_invest : WHY_DEFAULT;
  const heroStats = [
    { icon: Storefront, n: "120+", l: "Outlets" },
    { icon: MapPin, n: "25+", l: "Cities" },
    { icon: TrendUp, n: "2.5x", l: "Growth YoY" },
  ];
  const fins = [
    { l: "Revenue (FY25)", v: "₹28 Cr", delta: "+65%", bars: [40, 55, 70, 100] },
    { l: "EBITDA Margin", v: "22%", delta: "+4%", bars: [55, 60, 72, 88] },
    { l: "Store Count", v: "120+", delta: "+40%", bars: [50, 65, 80, 100] },
  ];

  return (
    <div data-testid="brand-detail-page">
      {/* breadcrumb */}
      <div className="px-6 md:px-12 lg:px-20 pt-6 text-xs text-ebg-muted">
        <Link to="/" className="hover:text-gold">Home</Link> <span className="mx-1">›</span>
        <Link to="/explore" className="hover:text-gold">Explore Brands</Link> <span className="mx-1">›</span>
        <span className="text-white">{brand.name}</span>
      </div>

      {/* hero */}
      <section className="px-6 md:px-12 lg:px-20 pt-6">
        <div className="relative rounded-2xl overflow-hidden h-[360px] md:h-[420px] border border-white/10">
          <img src={brand.cover} alt={brand.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/70 to-transparent" />
          <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-end">
            <span className="eyebrow mb-3">{brand.category}</span>
            <h1 className="font-display text-5xl md:text-6xl text-white tracking-tight">{brand.name}</h1>
            <p className="text-ebg-muted mt-2 text-lg">{brand.tagline}</p>
            <div className="flex flex-wrap items-center gap-8 mt-6">
              {heroStats.map((s) => (
                <div key={s.l} className="flex items-center gap-3">
                  <s.icon size={22} className="text-gold" weight="light" />
                  <div><p className="font-display text-2xl text-white leading-none">{s.n}</p><p className="text-[0.6rem] uppercase tracking-widest text-ebg-muted mt-1">{s.l}</p></div>
                </div>
              ))}
              <button data-testid="brand-film-btn" className="flex items-center gap-2 text-white hover:text-gold transition-colors ml-auto">
                <PlayCircle size={32} weight="light" /> <span className="text-sm">Watch Brand Film</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 md:px-12 lg:px-20 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-14">
          <div>
            <h2 className="font-display text-3xl text-white mb-4">About {brand.name}</h2>
            <p className="text-ebg-muted text-lg leading-relaxed">{brand.about}</p>
          </div>

          <div>
            <h2 className="font-display text-3xl text-white mb-6">Why Partner with {brand.name}?</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {why.map((w, i) => {
                const Icon = WHY_ICONS[i % WHY_ICONS.length];
                return (
                  <div key={w} className="card-franchise rounded-xl p-5">
                    <Icon size={22} className="text-gold" weight="light" />
                    <p className="text-sm text-white mt-3 leading-snug">{w}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h2 className="font-display text-3xl text-white mb-6">Financial Highlights</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {fins.map((f) => (
                <div key={f.l} className="card-franchise rounded-xl p-5">
                  <p className="text-[0.65rem] uppercase tracking-widest text-ebg-muted">{f.l}</p>
                  <div className="flex items-end justify-between mt-2">
                    <p className="font-display text-3xl text-white">{f.v}</p>
                    <span className="text-xs text-emerald-300">↑ {f.delta}</span>
                  </div>
                  <div className="flex items-end gap-1.5 h-10 mt-4">
                    {f.bars.map((b, i) => <span key={i} className="flex-1 rounded-t gold-gradient" style={{ height: `${b}%`, opacity: 0.4 + i * 0.18 }} />)}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-ebg-muted mt-3">Figures are indicative placeholders pending EBG finance vetting.</p>
          </div>

          {brand.locations?.length > 0 && (
            <div>
              <h2 className="font-display text-3xl text-white mb-4">Locations</h2>
              <div className="flex flex-wrap gap-3">
                {brand.locations.map((l) => (
                  <span key={l} className="bg-navy-900 border border-white/10 rounded-md px-4 py-2 text-sm text-white flex items-center gap-2"><MapPin size={14} className="text-gold" /> {l}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Investment Snapshot */}
        <aside className="lg:col-span-1">
          <div className="bg-navy-900 border border-gold/20 rounded-2xl p-6 lg:sticky lg:top-24">
            <h3 className="font-display text-2xl text-white mb-5">Partnership Snapshot</h3>
            <div className="space-y-4">
              {[["Participation Range", `${formatINR(brand.ticket_min)} – ${formatINR(brand.ticket_max)}`],
                ["Indicative Payout Range*", brand.expected_roi],
                ["Partnership Tenure", "3 – 5 Years"]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-white/10 pb-3">
                  <span className="text-sm text-ebg-muted">{k}</span><span className="text-sm text-white font-medium">{v}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pb-1">
                <span className="text-sm text-ebg-muted">Growth Stage</span><RiskBadge level={brand.risk_level} />
              </div>
            </div>
            <button onClick={goInvest} data-testid="brand-invest-now-btn" disabled={brand.status === "coming_soon"}
              className="w-full mt-6 btn-gold py-3.5 rounded-md flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              {brand.status === "coming_soon" ? "Coming Soon" : <>Express Interest <ArrowRight size={18} /></>}
            </button>
            <p className="text-xs text-ebg-muted mt-4 leading-relaxed">Express intent with a refundable fee of ₹5,000–₹10,000, adjusted against your final participation amount.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
